import React from 'react';
import {useIntl} from 'react-intl';
import {Button, FlexColumn, FlexRow, FlexItem, Text} from '@forecast-it/design-system';
import {NOVA_PURPLE} from './colors';
import {FieldInput, FieldSelect} from './styles';
import {SimpleUUID} from './constants';

// `T` is an optional passthrough payload — the caller's own domain object (e.g.
// ExtractedDeliverable, ExtractedInvoice) — carried through untouched by every list
// operation (rename/remove/add). Callers that supply it via `source` can reconstruct their
// domain array by spreading `item.source` and overwriting only `name`, instead of rebuilding
// every field from scratch on every edit. Callers that don't need it can ignore it entirely —
// it defaults to `unknown` and is optional on every item.
// A single inline-editable meta input for a row — e.g. an invoice's unit_price. Rendered
// instead of the static `meta` text for a row that supplies these, since a manually-added
// row (isNova: false, freshly built from makeBlank) has no Nova-supplied value to display
// and no way to change one that only ever renders as text.
export interface MetaFieldEditor {
	key: string;
	label: string;
	type?: 'text' | 'number' | 'date' | 'select';
	value: string;
	onChange: (value: string) => void;
	// Required when type is 'select'; ignored otherwise.
	options?: {value: string; label: string}[];
}

const META_FIELD_BORDER_COLOR = '#c4c4cf';

export interface ExtractedListItem<T = unknown> {
	id: string;
	name: string;
	meta: string;
	isNova: boolean;
	source?: T;
	// Present only for a row whose meta the caller wants inline-editable — rendered instead of
	// the static `meta` text. Absent (every Nova-extracted row today) leaves `meta` as
	// read-only display text, unchanged.
	metaFields?: MetaFieldEditor[];
}

interface EditableExtractedListProps<T = unknown> {
	items: ExtractedListItem<T>[];
	onChange: (items: ExtractedListItem<T>[]) => void;
	renderAddLabel: string;
	// Builds the passthrough payload for a freshly-added manual row. Omit if the caller
	// doesn't use `source`.
	makeBlank?: () => T;
}

/**
 * Add/edit/remove list for a set of extracted rows, shared by any section that shows
 * Nova-extracted items alongside manually-added ones (e.g. deliverables, invoicing terms).
 * Deliberately knows nothing about what `name`/`meta` mean for a given section — callers
 * map their domain objects to/from {id, name, meta, isNova, source} and own the field labels.
 *
 * Row identity: `id` is assigned once per row (by the caller for extracted rows, by this
 * component for a manually-added one) and must stay stable across re-renders regardless of
 * `name` edits — it is the React list key. Deriving it from mutable content (e.g. the row's
 * own name) causes React to remount the row's input on every keystroke, dropping focus.
 */
function EditableExtractedList<T = unknown>({items, onChange, renderAddLabel, makeBlank}: EditableExtractedListProps<T>) {
	const {formatMessage} = useIntl();

	const updateName = (index: number, name: string) => {
		onChange(items.map((item, i) => (i === index ? {...item, name} : item)));
	};

	const removeItem = (index: number) => {
		onChange(items.filter((_, i) => i !== index));
	};

	const addItem = () => {
		onChange([...items, {id: SimpleUUID(), name: '', meta: '', isNova: false, source: makeBlank?.()}]);
	};

	return (
		<FlexColumn gap="xs">
			{items.map((item, index) => {
				const nameInputId = `extracted-list-item-name-${item.id}`;
				return (
					<FlexRow key={item.id} gap="s" alignItems="center">
						<FlexItem flex={1}>
							<FieldInput
								id={nameInputId}
								type="text"
								aria-label={formatMessage({id: 'sow_review.row_name_input_label'}, {position: index + 1})}
								value={item.name}
								onChange={e => updateName(index, e.target.value)}
							/>
						</FlexItem>
						{item.metaFields && item.metaFields.length > 0 ? (
							<FlexRow gap="xs">
								{item.metaFields.map(field =>
									field.type === 'select' ? (
										<FieldSelect
											key={field.key}
											aria-label={field.label}
											value={field.value}
											onChange={e => field.onChange(e.target.value)}
											style={{width: 130, borderColor: META_FIELD_BORDER_COLOR}}
										>
											{(field.options ?? []).map(opt => (
												<option key={opt.value} value={opt.value}>
													{opt.label}
												</option>
											))}
										</FieldSelect>
									) : (
										<FieldInput
											key={field.key}
											type={field.type ?? 'text'}
											aria-label={field.label}
											value={field.value}
											onChange={e => field.onChange(e.target.value)}
											// Slightly darker than FieldInput's own default border (#e0e0e8) — at this
											// field's narrow width (100-150px, vs. the name field's full flex:1 width),
											// the default border is too faint to read as a box at rest, only becoming
											// visible via the focus ring once clicked.
											style={{
												width: field.type === 'date' ? 150 : 100,
												borderColor: META_FIELD_BORDER_COLOR,
											}}
										/>
									)
								)}
							</FlexRow>
						) : (
							<Text size="1" color="auxiliary" noWrap>
								{item.meta}
							</Text>
						)}
						{item.isNova && (
							// Plain inline span with NOVA_PURPLE, matching SowBanner's own ✦ icon
							// in SowReviewFormPanel.tsx — not the design system's `color="accent"`
							// token, which renders a different, mismatched purple here. No
							// aria-hidden: unlike SowBanner's icon (decorative, next to text that
							// already says "Nova"), this badge is the only signal a given row came
							// from Nova, so it stays in the accessibility tree.
							<span style={{color: NOVA_PURPLE, fontSize: 12}}>✦</span>
						)}
						{/* Icon-only (no children) so the row stays compact — BaseButton's own
						 * icon-only mode, not a custom button. Trade-off: BaseButton derives its
						 * aria-label from children?.toString() when present, but falls back to the
						 * icon name ("trash") when children is absent — a confirmed design-system
						 * behavior, not fixable here, so this button's accessible name reads as
						 * "trash" rather than "Remove". `title` gives sighted mouse users a hover
						 * tooltip with the clearer word; aria-describedby (not overwritten the same
						 * way aria-label is) still lets a screen-reader user tell one row's button
						 * apart from another's by pointing at that row's own name input. */}
						<Button
							emphasis="low"
							icon="trash"
							title={formatMessage({id: 'common.remove'})}
							aria-describedby={nameInputId}
							onPress={() => removeItem(index)}
						/>
					</FlexRow>
				);
			})}
			<Button emphasis="low" icon="plus" onPress={addItem}>
				{renderAddLabel}
			</Button>
		</FlexColumn>
	);
}

export default EditableExtractedList;
